import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Residential Painting',
    description: 'Complete interior and exterior painting for Plattekloof homes',
    price: 'From R9,000',
    features: ['Quality paints', 'Surface preparation', 'Clean finish', '2-year warranty']
  },
  {
    icon: 'ri-building-line',
    title: 'Commercial Painting',
    description: 'Professional painting for offices and commercial properties',
    price: 'From R13,000',
    features: ['Flexible scheduling', 'Minimal disruption', 'Durable finishes', 'Fast completion']
  },
  {
    icon: 'ri-drop-line',
    title: 'Waterproofing',
    description: 'Comprehensive waterproofing and damp-proofing solutions',
    price: 'From R7,500',
    features: ['Leak prevention', 'Damp treatment', 'Long-lasting protection', '5-year guarantee']
  }
];

const paintingProcess = [
  { step: '1', title: 'Free Quote', description: 'Property assessment and detailed quotation' },
  { step: '2', title: 'Preparation', description: 'Surface prep and area protection' },
  { step: '3', title: 'Painting', description: 'Professional application with quality materials' },
  { step: '4', title: 'Completion', description: 'Final inspection and client approval' }
];

const testimonials = [
  {
    rating: 5,
    text: 'Great experience from start to finish! The team was professional, efficient, and the quality of work was excellent. Our Plattekloof home looks fantastic.',
    name: 'Pieter Smit',
    property: 'Plattekloof Homeowner',
    image: 'https://public.readdy.ai/ai/img_res/aaaa856893d67fbc5fe34c7c68131ad5.jpg'
  },
  {
    rating: 5,
    text: 'Very happy with the results! They were punctual, clean, and delivered high-quality work. Would definitely recommend to anyone in Plattekloof.',
    name: 'Amanda Nel',
    property: 'Plattekloof Resident',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20woman%20homeowner%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-plattekloof-002&orientation=squarish'
  },
  {
    rating: 5,
    text: 'Excellent service and workmanship. The painters were skilled, respectful, and completed the job on time. Our property looks amazing!',
    name: 'Francois du Plessis',
    property: 'Business Owner',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20businessman%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-plattekloof-003&orientation=squarish'
  }
];

export default function PlattekloofPage() {
  useEffect(() => {
    document.title = 'Plattekloof Painting Services | Professional Painters Cape Town';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services in Plattekloof, Cape Town. Residential, commercial, and estate painting solutions. Professional painters with 15+ years experience. Free quotes available.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Plattekloof painters, painting services Plattekloof, house painters Plattekloof, commercial painters Cape Town, residential painting');
    }

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (metaGeoPosition) {
      metaGeoPosition.setAttribute('content', '-33.8456;18.5789');
    }

    const metaGeoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (metaGeoPlacename) {
      metaGeoPlacename.setAttribute('content', 'Plattekloof, Cape Town, Western Cape, South Africa');
    }

    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (metaGeoRegion) {
      metaGeoRegion.setAttribute('content', 'ZA-WC');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20home%20in%20Plattekloof%20Cape%20Town%2C%20skilled%20painters%20on%20scaffolding%20applying%20paint%20to%20house%20walls%2C%20beautiful%20residential%20property%2C%20sunny%20day%2C%20professional%20equipment%2C%20high%20quality%20workmanship%2C%20realistic%20photography&width=1920&height=1080&seq=plattekloof-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Plattekloof
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Plattekloof's residential and commercial properties. Quality craftsmanship and reliable service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Area Description */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Painting Experts Serving Plattekloof</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Plattekloof is a vibrant residential area in Cape Town's Northern Suburbs, known for its modern homes, excellent schools, and family-oriented community. Our professional painting services are designed to meet the diverse needs of Plattekloof painting services for residential properties, commercial spaces, and community facilities.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                With over 15 years of experience serving Plattekloof and surrounding areas, we understand the specific requirements of properties in this growing suburb. From contemporary townhouses to established family homes, our team delivers high-quality painting solutions that enhance curb appeal and protect your investment.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you need interior painting for your Plattekloof home, exterior painting for commercial properties, or specialized waterproofing services, our experienced painters use premium materials and professional techniques to ensure durable, beautiful results that stand the test of time.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20team%20working%20on%20modern%20house%20exterior%20in%20Plattekloof%20suburb%2C%20showing%20skilled%20craftsmen%20applying%20paint%20with%20professional%20equipment%2C%20contemporary%20house%20design%2C%20quality%20painting%20work%2C%20natural%20daylight%2C%20professional%20service%20photography&width=600&height=500&seq=plattekloof-team-002&orientation=portrait"
                alt="Professional Painters in Plattekloof"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Plattekloof Painting Services</h2>
            <p className="text-xl text-gray-600">Comprehensive painting solutions for every need</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-black font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line mr-2" style={{color: '#ffff00'}}></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Plattekloof Residents Choose Us</h2>
            <p className="text-xl text-gray-600">Trusted painting professionals in your community</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">15+ Years Experience</h3>
              <p className="text-gray-600">Extensive experience serving Plattekloof community</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600">Premium materials and comprehensive warranty</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Team</h3>
              <p className="text-gray-600">Skilled and certified painting specialists</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-customer-service-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Excellent Service</h3>
              <p className="text-gray-600">Responsive communication and customer care</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Painting Process</h2>
            <p className="text-xl text-gray-600">Professional service from start to finish</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Recent Projects in Plattekloof</h2>
            <p className="text-xl text-gray-600">Quality workmanship in your neighborhood</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Contemporary%20residential%20house%20exterior%20with%20fresh%20paint%20in%20modern%20colors%20in%20Plattekloof%20suburb%2C%20showing%20clean%20architectural%20lines%2C%20well-maintained%20garden%2C%20professional%20painting%20finish%2C%20bright%20natural%20lighting&width=400&height=300&seq=plattekloof-project-001&orientation=landscape"
                  alt="Residential Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Modern Home Exterior</h3>
              <p className="text-gray-600">Complete exterior painting with weather-resistant coating</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Commercial%20retail%20space%20interior%20with%20freshly%20painted%20walls%20in%20Plattekloof%2C%20showing%20modern%20commercial%20design%2C%20clean%20paint%20finish%2C%20professional%20lighting%2C%20quality%20commercial%20painting%20work&width=400&height=300&seq=plattekloof-project-002&orientation=landscape"
                  alt="Commercial Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Retail Space Refresh</h3>
              <p className="text-gray-600">Professional commercial painting for local business</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Residential%20interior%20bedroom%20with%20freshly%20painted%20walls%20in%20soft%20neutral%20tones%2C%20showing%20modern%20decor%2C%20clean%20paint%20finish%2C%20natural%20window%20lighting%2C%20professional%20interior%20painting%20in%20Plattekloof%20home&width=400&height=300&seq=plattekloof-project-003&orientation=landscape"
                  alt="Interior Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Interior Makeover</h3>
              <p className="text-gray-600">Complete interior painting with modern color palette</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Residential%20townhouse%20complex%20in%20Plattekloof%20with%20freshly%20painted%20exteriors%2C%20showing%20multiple%20units%20with%20coordinated%20colors%2C%20neat%20landscaping%2C%20professional%20painting%20work%2C%20clear%20blue%20sky&width=400&height=300&seq=plattekloof-project-004&orientation=landscape"
                  alt="Townhouse Complex Painting"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Townhouse Complex</h3>
              <p className="text-gray-600">Multi-unit painting with coordinated color scheme</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Plattekloof Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by homeowners and businesses in Plattekloof</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{color: '#ffff00'}}></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#ffff00'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Plattekloof Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Plattekloof's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}